import React from "react"
import useAppStore from "../../../stores/AppStore"
import { CampoTextoImpressao } from "../context/textoImpressaoActions"
import { useTextoImpressaoContext } from "../context/useTextoImpressaoContext"

type LinhaProps = {
    campo: CampoTextoImpressao
    children: React.ReactNode
}

type LinhaValorProps = {
    campo: CampoTextoImpressao
    label: string
    valor: string
}

const FONTE_PX = [13, 20, 24]
const LINHA_PX = [20, 28, 36]
const ESPACO_PX = [8, 8, 16]
const SCALE_X = [1, 1, 1]
const DIVISOR_COLUNAS = [1, 1, 2]

function quebrarLinhas(texto: string, maxChars: number): string {
    if (maxChars <= 0 || texto.length <= maxChars) return texto
    const linhas: string[] = []
    for (let i = 0; i < texto.length; i += maxChars) {
        linhas.push(texto.slice(i, i + maxChars))
    }
    return linhas.join("\n")
}

function estiloBase(config: any, pg: any) {
    const nivel = Math.min(2, Math.max(0, Math.max(config.fonte || 0, pg?.fonte || 0)))
    const negrito = (pg?.negrito ?? false) || config.negrito
    const scaleX = SCALE_X[nivel]
    const colunas = pg?.colunas ?? 40
    const margemEsq = config.margemEsquerda || 0
    const maxChars = Math.floor((colunas - margemEsq) / DIVISOR_COLUNAS[nivel])
    return {
        nivel,
        scaleX,
        maxChars,
        style: {
            padding: 0,
            fontFamily: "monospace" as const,
            marginTop: `${(config.margemSuperior || 0) * LINHA_PX[nivel]}px`,
            marginBottom: `${(config.margemInferior || 0) * LINHA_PX[nivel]}px`,
            marginLeft: `${(config.margemEsquerda || 0) * ESPACO_PX[nivel]}px`,
            fontSize: `${FONTE_PX[nivel]}px`,
            fontWeight: negrito ? ("bold" as const) : ("normal" as const),
        },
    }
}

function LinhaTexto({ campo, children }: LinhaProps) {
    const { textoImpressao } = useTextoImpressaoContext()
    const config = textoImpressao[campo]
    const pg = textoImpressao.parametrosGerais

    if (!config?.ativo) return null

    const { style, scaleX, maxChars } = estiloBase(config, pg)
    const inicio = pg?.textoInicioLinha || ""
    const texto = quebrarLinhas(`${inicio}${String(children)}`, maxChars)

    return (
        <p style={{ ...style, overflow: "hidden", whiteSpace: "pre-wrap" }}>
            <span style={{ display: "inline-block", transform: `scaleX(${scaleX})`, transformOrigin: "left center" }}>
                {texto}
            </span>
        </p>
    )
}

function LinhaValor({ campo, label, valor }: LinhaValorProps) {
    const { textoImpressao } = useTextoImpressaoContext()
    const config = textoImpressao[campo]
    const pg = textoImpressao.parametrosGerais

    if (!config?.ativo) return null

    const { style, scaleX, maxChars } = estiloBase(config, pg)
    const inicio = pg?.textoInicioLinha || ""
    const valorDireita = config.valorDireita ?? false

    if (valorDireita) {
        const textoLabel = quebrarLinhas(`${inicio}${label}`, maxChars)
        return (
            <p style={{ ...style, overflow: "hidden" }}>
                <span style={{ display: "flex", justifyContent: "space-between", transform: `scaleX(${scaleX})`, transformOrigin: "left center" }}>
                    <span style={{ whiteSpace: "pre-wrap" }}>{textoLabel}</span>
                    <span>{valor}</span>
                </span>
            </p>
        )
    }

    const texto = quebrarLinhas(`${inicio}${label} ${valor}`, maxChars)
    return (
        <p style={{ ...style, overflow: "hidden", whiteSpace: "pre-wrap" }}>
            <span style={{ display: "inline-block", transform: `scaleX(${scaleX})`, transformOrigin: "left center" }}>
                {texto}
            </span>
        </p>
    )
}

export function Notinha() {
    const { textoImpressao } = useTextoImpressaoContext()
    const urlLogo = useAppStore(s => s.configuracoes?.user?.parametros?.estabelecimento?.urlLogo)
    const colunas = textoImpressao.parametrosGerais?.colunas ?? 40
    const sep = "_".repeat(colunas)
    const exibirLogo = textoImpressao.parametrosGerais?.exibirLogo ?? false

    return (
        <div
            style={{
                backgroundColor: "#FFF9C4",
                padding: "1.2rem 1.2rem",
                fontFamily: "monospace",
            }}
        >
            {exibirLogo && urlLogo && (
                <div style={{ display: "flex", justifyContent: "center", marginBottom: "0.5rem" }}>
                    <img
                        src={urlLogo}
                        alt="Logo"
                        style={{ maxWidth: "120px", maxHeight: "80px", objectFit: "contain" }}
                    />
                </div>
            )}
            <p>{sep}</p>

            <LinhaTexto campo="idDoPedido">#********* (central de pedidos)</LinhaTexto>
            <LinhaTexto campo="data">Data: DD/MM/AAAA Hora 00:00</LinhaTexto>
            <LinhaTexto campo="cliente">Cliente: *****</LinhaTexto>
            <LinhaTexto campo="cpf">CPF: ***.***.***-**</LinhaTexto>
            <LinhaTexto campo="telefone">Telefone: (**) *****-****</LinhaTexto>
            <LinhaTexto campo="previsaoEntrega">Previsão Entrega: 00:00</LinhaTexto>
            <LinhaTexto campo="rua">Cidade: *****</LinhaTexto>
            <LinhaTexto campo="numeroRua">Numero: ***</LinhaTexto>
            <LinhaTexto campo="bairro">Bairro: *****</LinhaTexto>
            <LinhaTexto campo="cidade">Rua: ****</LinhaTexto>
            <LinhaTexto campo="avisoCartela">Cartela Completa</LinhaTexto>
            <LinhaTexto campo="pedidosAnteriores">Qtd. Pedidos Anteriores: **</LinhaTexto>

            <p>{sep}</p>

            <LinhaTexto campo="categoria">Hambúrguer</LinhaTexto>
            <LinhaTexto campo="produto">1 X X-burguer</LinhaTexto>
            <LinhaTexto campo="grupoDeOpcao">Batata</LinhaTexto>
            <LinhaTexto campo="opcao">{">"} 1 X Batata Grande</LinhaTexto>
            <LinhaTexto campo="observacaoProduto">OBS.: Sem cebola</LinhaTexto>
            <LinhaTexto campo="observacaoPedido">OBS.: Não bater interfone</LinhaTexto>

            <p>{sep}</p>

            <LinhaValor campo="pagamento" label="Pagamento:" valor="*************" />
            <LinhaValor campo="subtotal" label="Subtotal:" valor="R$ **,**" />
            <LinhaValor campo="taxaDeEntrega" label="Taxa de entrega:" valor="R$ *,**" />
            <LinhaValor campo="desconto" label="Desconto:" valor="R$ *,**" />
            <LinhaValor campo="taxaAdicional" label="Taxa adicional:" valor="R$ *,**" />
            <LinhaValor campo="total" label="TOTAL:" valor="R$ **,**" />

            <p>{sep}</p>

            <LinhaTexto campo="cnpj">CNPJ: **.***.***/****.** </LinhaTexto>
            <LinhaTexto campo="nomeEstabelecimento">Estabelecimento</LinhaTexto>
            <LinhaTexto campo="numeroEstabelecimento">(**) *****-****</LinhaTexto>
        </div>
    )
}
